import { readonly, ref, toRefs } from 'vue';
import posthog from 'posthog-js';

import { useAuthStore } from '@/stores/auth.store';

import { isConsentBannerEnabled, isPostHogEnabled } from '@/utils/flags';
import { CONSENT_VERSION } from '@/common/legal';
import { stringToBool } from '@/common/utils/string.utils';

import { UserOrganisation } from '@/types/api/auth.types';
import { AnalyticsMetadata, InteractionType } from '@/types/plugins/analytics.types';
import { selectedOrgRoles } from '@/common/utils/role.utils';

const CONSENT_KEY = 'ph_consent_status';
const USER_ID_KEY = 'ph_user_id';

/**
 * Helper to get a user-specific consent key.
 * Example: 'ph_consent_status_123'
 */
export const getScopedConsentKey = (userId: string): string => `${CONSENT_KEY}_${userId}`;

/**
 * Read the consent version a user has accepted from localStorage.
 * - missing / opted-out ('false') → 0 (never accepted)
 * - legacy 'true' (pre-versioning) → 1 (migrated, counts as the first version)
 * - a stored number → that version
 */
const getAcceptedConsentVersion = (userId: string): number => {
  const raw = localStorage.getItem(getScopedConsentKey(userId));
  if (!raw || raw === 'false') return 0;
  if (raw === 'true') return 1; // legacy record, pre-versioning
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Whether the user has accepted the CURRENT consent version.
 */
export const hasAcceptedCurrentConsent = (userId: string): boolean =>
  getAcceptedConsentVersion(userId) >= CONSENT_VERSION;

/**
 * Reactive mirror of the current user's accepted consent version — localStorage
 * isn't reactive, so consent-gated UI reads this to update without a reload.
 * Kept in sync at every write site below; seed it via `refreshAcceptedConsentVersion`.
 */
const acceptedConsentVersionRef = ref(0);
export const acceptedConsentVersion = readonly(acceptedConsentVersionRef);

/** Recompute the reactive accepted-version mirror from localStorage for a user. */
export const refreshAcceptedConsentVersion = (userId?: string): void => {
  acceptedConsentVersionRef.value = userId ? getAcceptedConsentVersion(userId) : 0;
};

/**
 * Build the PostHog Person profile payload from the current auth user. Kept in
 * one place so `setConsent` and `identify` can't drift. Callers MUST have opted
 * in and identified as the DB user id first, so these props never attach to the
 * anonymous device person (which would split the user into two profiles).
 */
const buildPersonProperties = () => {
  const { loggedInUser } = toRefs(useAuthStore());
  return {
    consent_status: true,
    consent_version: CONSENT_VERSION,
    last_login: new Date().toISOString(),
    email: loggedInUser.value?.email,
    active_org_id: loggedInUser.value?.selectedOrg?.id,
    active_org_name: loggedInUser.value?.selectedOrg?.name,
    active_org_role: loggedInUser.value?.selectedOrg?.userRole,
    active_org_roles: selectedOrgRoles.value.join(','),
    total_orgs_count: loggedInUser.value?.organisations?.length ?? 0,
  };
};

export const analyticsService = {
  /**
   * Initialize PostHog in "Quiet Mode"
   */
  init(): void {
    // Check if the environment allows for tracking
    if (!isPostHogEnabled) {
      console.info('[Analytics] PostHog is disabled in this environment.');
      return;
    }

    posthog.init(import.meta.env.VITE_APP_POSTHOG_KEY, {
      api_host: import.meta.env.VITE_APP_POSTHOG_HOST || 'https://eu.posthog.com',
      autocapture: false, // Disable automatic click tracking
      capture_pageview: false, // Disable automatic pageview tracking
      disable_session_recording: true, // Disable Replays for V1
      persistence: 'localStorage',
      loaded: (ph) => {
        // Default to safe mode on boot
        ph.opt_out_capturing();
      },
    });
    console.info('[Analytics] PostHog initialized.');
  },

  /**
   * Logic to check if the CURRENT user has consented.
   */
  isCurrentUserConsented(): boolean {
    const { loggedInUser } = toRefs(useAuthStore());

    let userId = loggedInUser.value?.id;

    if (!userId) {
      userId = localStorage.getItem(USER_ID_KEY) || '';
    }

    if (!userId) {
      return false;
    }

    return hasAcceptedCurrentConsent(userId);
  },

  /**
   * Manual event capture with consent check
   */
  capture(eventName: string, properties = {}): void {
    if (this.isCurrentUserConsented() && isPostHogEnabled) {
      posthog.capture(eventName, properties);
    }
  },

  /**
   * Set consent and update local storage
   */
  setConsent(status: string): void {
    const { loggedInUser } = toRefs(useAuthStore());
    const consented = stringToBool(status);

    // Local consent record (legal acceptance) — decoupled from analytics capture
    // so it works outside production. Persist the accepted version (not just
    // 'true') so a future CONSENT_VERSION bump re-triggers the banner; opting out
    // stores 'false'.
    if (isConsentBannerEnabled && loggedInUser.value?.id) {
      console.info('[Analytics] Setting consent status to:', status);
      localStorage.setItem(
        getScopedConsentKey(loggedInUser.value.id),
        consented ? String(CONSENT_VERSION) : 'false',
      );
      localStorage.setItem(USER_ID_KEY, loggedInUser.value.id);
      acceptedConsentVersionRef.value = consented ? CONSENT_VERSION : 0;
    }

    // PostHog analytics opt-in/out and remote profile sync stay production-only.
    if (isPostHogEnabled) {
      if (consented) {
        const userId = loggedInUser.value?.id;
        // Only touch PostHog when we have the DB user id, and always in order:
        // opt in → identify → set profile. Writing person properties (email,
        // consent_version) before a confirmed identify attaches them to the
        // anonymous device person, which splits the user into two profiles.
        if (userId) {
          posthog.opt_in_capturing();
          posthog.identify(userId);
          // Save this to the remote profile for multi-device sync
          posthog.setPersonProperties(buildPersonProperties());
        }
      } else {
        posthog.opt_out_capturing();
      }
    }
  },

  /**
   * Identity Management
   */
  identify(login?: boolean): void {
    const { loggedInUser } = toRefs(useAuthStore());

    if (isPostHogEnabled && this.isCurrentUserConsented() && loggedInUser.value?.id) {
      // Opt in before identify so the $identify merge event isn't suppressed,
      // then link the PostHog ID to our DB ID.
      posthog.opt_in_capturing();
      posthog.identify(loggedInUser.value.id);

      // Syncs the status to the remote 'Person' profile
      // This ensures that even on a new device, PostHog knows this user has consented.
      posthog.setPersonProperties(buildPersonProperties());

      if (login) {
        analyticsService.capture('user_logged_in', {
          user_role: loggedInUser.value?.selectedOrg?.userRole,
          user_roles: selectedOrgRoles.value.join(','),
          org_id: loggedInUser.value?.selectedOrg?.id,
          org_name: loggedInUser.value?.selectedOrg?.name,
        });
      }

      localStorage.setItem(USER_ID_KEY, loggedInUser.value.id);
    }
  },

  /**
   * Syncs consent from PostHog using the Feature Flag "Bridge" approach if localStorage is empty.
   * Called during the Login/App initialization sequence.
   * @param login When true, indicates this sync is triggered from the login flow and
   * marks analytics consent sync as checked for the current login session.
   */
  async syncConsentFromRemote(login?: boolean): Promise<boolean> {
    const { loggedInUser, analyticsSyncCheckedOnLogin } = toRefs(useAuthStore());

    if (!isPostHogEnabled) {
      return false;
    }

    if (login) {
      analyticsSyncCheckedOnLogin.value = true;
    }

    const userId = loggedInUser.value?.id;
    if (!userId) {
      return false;
    }

    try {
      // Identify only to scope the `has-consented` flag to this user. Do NOT opt
      // in here: this runs for users who haven't consented yet, and enabling
      // capture pre-consent would be wrong. No person properties are written
      // while opted out, so this can't spawn a second profile — the opt-in +
      // profile write happens below only once remote consent is confirmed.
      posthog.identify(userId);

      const hasConsent = await new Promise<boolean>((resolve) => {
        let resolved = false;

        const timeoutId = setTimeout(() => {
          if (resolved) {
            return;
          }
          resolved = true;
          console.warn('[Analytics] Feature flag load timeout.');
          resolve(false);
        }, 5000);

        posthog.onFeatureFlags(() => {
          if (resolved) {
            return;
          }
          resolved = true;
          clearTimeout(timeoutId);

          const hasConsentedFlag = posthog.isFeatureEnabled('has-consented');
          resolve(!!hasConsentedFlag);
        });

        // trigger reload
        posthog.reloadFeatureFlags();
      });

      if (hasConsent) {
        console.info('[Analytics] Remote consent verified.');

        // `has-consented` must be configured in PostHog to match
        // `consent_version >= CONSENT_VERSION`, so a `true` here means the user
        // accepted the CURRENT version — safe to stamp it locally. If the flag
        // still keys on the legacy `consent_status` boolean this is equivalent
        // for version 1, but MUST be updated before bumping CONSENT_VERSION,
        // otherwise old-version consenters never see the refreshed banner.
        localStorage.setItem(getScopedConsentKey(userId), String(CONSENT_VERSION));
        localStorage.setItem(USER_ID_KEY, userId);
        acceptedConsentVersionRef.value = CONSENT_VERSION;

        // Opt in → identify → set profile (same order as setConsent()/identify()):
        // now that remote consent is confirmed, refresh the person profile on this
        // device instead of relying on whatever the device they consented on wrote.
        posthog.opt_in_capturing();
        posthog.identify(userId);
        posthog.setPersonProperties(buildPersonProperties());

        return true;
      }

      console.info('[Analytics] No remote consent flag found.');
      return false;
    } catch (error) {
      console.error('[Analytics] Remote consent sync failed:', error);
      return false;
    }
  },

  /**
   * Tracks UI expansion events (Drawers/Modals)
   */
  trackUIInteraction(name: string, type: InteractionType, metadata: AnalyticsMetadata): void {
    if (isPostHogEnabled && this.isCurrentUserConsented()) {
      this.capture(name, {
        interaction_type: type,
        ...metadata,
      });
    }
  },

  /**
   * Tracks when a user switches context between different organisations.
   */
  trackOrgSwitch(newOrg: UserOrganisation, fromOrgId?: string): void {
    if (isPostHogEnabled && this.isCurrentUserConsented()) {
      const { loggedInUser } = toRefs(useAuthStore());

      // Read the scalar role from the normalized auth state, not the newOrg arg:
      // setSelectedOrg has already run normalizeUserRoles (which can promote
      // selectedOrg.userRole), so newOrg.userRole may be stale relative to the
      // active_org_roles array sourced from roles.organisations below.
      const activeOrgRole = loggedInUser.value?.selectedOrg?.userRole;

      // Look both orgs' full role sets up from roles.organisations (which holds
      // all orgs, so the outgoing org's roles are still available after the switch).
      const orgRolesFor = (orgId?: string): string =>
        (loggedInUser.value?.roles?.organisations?.find((o) => o.id === orgId)?.roles ?? []).join(
          ',',
        );

      // Update Person Properties so future events
      // are tagged with the NEW org automatically in PostHog's filters.
      posthog.setPersonProperties({
        active_org_id: newOrg.id,
        active_org_name: newOrg.name,
        active_org_role: activeOrgRole,
        active_org_roles: orgRolesFor(newOrg.id),
      });

      analyticsService.capture('org_switched', {
        to_org_id: newOrg.id,
        to_org_name: newOrg.name,
        to_org_role: activeOrgRole,
        to_org_roles: orgRolesFor(newOrg.id),
        from_org_roles: orgRolesFor(fromOrgId),
      });
    }
  },

  reset(): void {
    const { analyticsSyncCheckedOnLogin } = toRefs(useAuthStore());

    // Clear the consent mirror on logout in every env (PostHog reset below is prod-only).
    acceptedConsentVersionRef.value = 0;

    if (!isPostHogEnabled) {
      return;
    }

    if (this.isCurrentUserConsented()) {
      this.capture('user_logged_out');
      analyticsSyncCheckedOnLogin.value = false;
    }

    posthog.reset();
  },
};
